import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, LogOut, Trash2, Plus, Pencil, X, Upload, Mail, Download, Check, ChevronDown } from "lucide-react";
import { fr, chf, num } from "../content/fr";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import {
  formatApiError, adminStats, adminLeads, adminOrders, adminConfig, adminUpdateConfig,
  adminAngles, adminCreateAngle, adminUpdateAngle, adminDeleteAngle, uploadProof, BACKEND,
  adminPartners, adminChallenge, adminAudits, setBilanStatus,
} from "../lib/api";
import ContentPanel from "../components/admin/ContentPanel";

function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try { await login(email, password); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || "Échec de connexion"); setLoading(false); }
  };

  return (
    <div className="login-wrap">
      <form className="glass login-card" onSubmit={submit} data-testid="admin-login-form">
        <Link to="/" className="logo" style={{ marginBottom: "1.5rem" }}><span className="logo__mark"><Sparkles size={16} /></span>{fr.brand.name}</Link>
        <h1 className="h3">{fr.admin.loginTitle}</h1>
        <p className="muted" style={{ margin: "0.5rem 0 1.5rem", fontSize: "0.9rem" }}>{fr.admin.loginSub}</p>
        <div className="field" style={{ marginBottom: "1rem" }}>
          <label htmlFor="a-email">{fr.admin.email}</label>
          <input id="a-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" />
        </div>
        <div className="field" style={{ marginBottom: "1.25rem" }}>
          <label htmlFor="a-pass">{fr.admin.password}</label>
          <input id="a-pass" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" />
        </div>
        <button className="btn btn--primary btn--block" disabled={loading} data-testid="admin-login-submit">{loading ? "Connexion…" : fr.admin.loginBtn}</button>
        {error && <p className="form-status err" data-testid="admin-login-error">{error}</p>}
      </form>
    </div>
  );
}

function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { adminStats().then(setS).catch(() => {}); }, []);
  if (!s) return <p className="muted">Chargement…</p>;
  const cards = [
    { l: "Leads", v: num(s.leads) }, { l: "Commandes", v: num(s.orders_total) },
    { l: "Commandes payées", v: num(s.orders_paid) }, { l: "Revenu (démo)", v: chf(s.revenue) },
  ];
  return (
    <div data-testid="admin-dashboard">
      <div className="stat-grid">
        {cards.map((c) => (<div className="glass stat" key={c.l}><div className="v gradient-text tnum">{c.v}</div><div className="l">{c.l}</div></div>))}
      </div>
      <p className="muted" style={{ fontSize: "0.85rem" }}>Les compteurs publics ne peuvent être publiés qu'avec une preuve (voir Configuration).</p>
    </div>
  );
}

function DataList({ load, columns, testid, render }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { load().then(setRows).catch(() => setRows([])); }, []); // eslint-disable-line
  if (!rows) return <p className="muted">Chargement…</p>;
  if (rows.length === 0) return <p className="muted" data-testid={`${testid}-empty`}>Aucune donnée.</p>;
  return (
    <div className="glass" style={{ overflowX: "auto" }} data-testid={testid}>
      <table className="data-table">
        <thead><tr>{columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
        <tbody>{rows.map(render)}</tbody>
      </table>
    </div>
  );
}

function ConfigPanel() {
  const [cfg, setCfg] = useState(null);
  const [msg, setMsg] = useState(null);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { adminConfig().then(setCfg).catch(() => {}); }, []);
  if (!cfg) return <p className="muted">Chargement…</p>;

  const setPrice = (k, v) => setCfg((c) => ({ ...c, prices: { ...c.prices, [k]: v } }));
  const setThr = (v) => setCfg((c) => ({ ...c, thresholds: { ...c.thresholds, vsl_cta_unlock_seconds: v } }));
  const setCnt = (k, v) => setCfg((c) => ({ ...c, counters: { ...c.counters, [k]: v } }));
  const setVsl = (v) => setCfg((c) => ({ ...c, vsl_video_url: v }));
  const setBilan = (k, v) => setCfg((c) => ({ ...c, bilan: { ...(c.bilan || {}), [k]: v } }));
  const setRemise = (v) => setCfg((c) => ({ ...c, agences: { ...(c.agences || {}), remise_pct: v } }));
  const plans = (cfg.agences || {}).plans || [];
  const setPlan = (i, k, v) => setCfg((c) => { const pl = [...(((c.agences || {}).plans) || [])]; pl[i] = { ...pl[i], [k]: v }; return { ...c, agences: { ...(c.agences || {}), plans: pl } }; });
  const addPlan = () => setCfg((c) => ({ ...c, agences: { ...(c.agences || {}), plans: [...(((c.agences || {}).plans) || []), { nom: "", prix: 0 }] } }));
  const removePlan = (i) => setCfg((c) => ({ ...c, agences: { ...(c.agences || {}), plans: (((c.agences || {}).plans) || []).filter((_, j) => j !== i) } }));
  const chapters = cfg.vsl_chapters || [];
  const setChapter = (i, k, v) => setCfg((c) => { const ch = [...(c.vsl_chapters || [])]; ch[i] = { ...ch[i], [k]: v }; return { ...c, vsl_chapters: ch }; });
  const addChapter = () => setCfg((c) => ({ ...c, vsl_chapters: [...(c.vsl_chapters || []), { titre: "", time: 0 }] }));
  const removeChapter = (i) => setCfg((c) => ({ ...c, vsl_chapters: (c.vsl_chapters || []).filter((_, j) => j !== i) }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true); setMsg(null);
    try { const r = await uploadProof(file); setCnt("proof_url", BACKEND + r.url); setMsg({ ok: true, t: "Preuve téléversée." }); }
    catch { setMsg({ ok: false, t: "Échec de l'upload." }); }
    setUploading(false);
  };

  const save = async () => {
    setMsg(null);
    try {
      const updated = await adminUpdateConfig({
        prices: { pilote: Number(cfg.prices.pilote), character_bible: Number(cfg.prices.character_bible), upsell: Number(cfg.prices.upsell) },
        thresholds: { vsl_cta_unlock_seconds: Number(cfg.thresholds.vsl_cta_unlock_seconds) },
        vsl_video_url: cfg.vsl_video_url || "",
        vsl_chapters: (cfg.vsl_chapters || []).map((c) => ({ titre: c.titre, time: Number(c.time) || 0 })),
        meta_pixel_id: cfg.meta_pixel_id || "",
        meta_capi_token: cfg.meta_capi_token || "",
        counters: {
          videos_produites: cfg.counters.videos_produites ? Number(cfg.counters.videos_produites) : null,
          comptes_actifs: cfg.counters.comptes_actifs ? Number(cfg.counters.comptes_actifs) : null,
          proof_url: cfg.counters.proof_url || null,
        },
        bilan: { taux_horaire: Number(cfg.bilan?.taux_horaire) || 50, prix_viralya_ref: Number(cfg.bilan?.prix_viralya_ref) || 197 },
        agences: { remise_pct: Number(cfg.agences?.remise_pct) || 0, plans: ((cfg.agences?.plans) || []).map((p) => ({ nom: p.nom, prix: Number(p.prix) || 0 })) },
      });
      setCfg(updated); setMsg({ ok: true, t: "Configuration enregistrée." });
    } catch (e) { setMsg({ ok: false, t: formatApiError(e.response?.data?.detail) }); }
  };

  return (
    <div className="glass card" data-testid="admin-config">
      <h3 className="h3" style={{ marginBottom: "1rem" }}>Prix (CHF)</h3>
      <div className="form-grid">
        <div className="field"><label>Programme Pilote</label><input type="number" value={cfg.prices.pilote} onChange={(e) => setPrice("pilote", e.target.value)} data-testid="cfg-price-pilote" /></div>
        <div className="field"><label>Character Bible</label><input type="number" value={cfg.prices.character_bible} onChange={(e) => setPrice("character_bible", e.target.value)} data-testid="cfg-price-bible" /></div>
        <div className="field"><label>Upsell (Pack Accélération)</label><input type="number" value={cfg.prices.upsell} onChange={(e) => setPrice("upsell", e.target.value)} data-testid="cfg-price-upsell" /></div>
      </div>

      <h3 className="h3" style={{ margin: "1.5rem 0 1rem" }}>Vidéo VSL</h3>
      <div className="form-grid">
        <div className="field full"><label>URL de la vidéo (mp4 / hébergée)</label><input value={cfg.vsl_video_url ?? ""} onChange={(e) => setVsl(e.target.value)} placeholder="https://…/vsl.mp4" data-testid="cfg-vsl-url" /></div>
        <div className="field"><label>Déblocage CTA (secondes)</label><input type="number" value={cfg.thresholds.vsl_cta_unlock_seconds} onChange={(e) => setThr(e.target.value)} data-testid="cfg-vsl-threshold" /></div>
      </div>
      <div style={{ marginTop: "0.75rem" }}>
        <label className="mono muted" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>Chapitres</label>
        {chapters.map((c, i) => (
          <div key={i} style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem", alignItems: "center" }}>
            <input style={{ flex: 2 }} placeholder="Titre" value={c.titre} onChange={(e) => setChapter(i, "titre", e.target.value)} data-testid={`cfg-chapter-title-${i}`} />
            <input style={{ flex: 1 }} type="number" placeholder="sec" value={c.time} onChange={(e) => setChapter(i, "time", e.target.value)} data-testid={`cfg-chapter-time-${i}`} />
            <button className="icon-btn" style={{ width: 36, height: 36 }} onClick={() => removeChapter(i)} aria-label="Retirer"><X size={14} /></button>
          </div>
        ))}
        <button className="btn btn--ghost" style={{ marginTop: "0.6rem", minHeight: 40 }} onClick={addChapter} data-testid="cfg-add-chapter"><Plus size={14} /> Ajouter un chapitre</button>
      </div>

      <h3 className="h3" style={{ margin: "1.5rem 0 1rem" }}>Tracking (Meta Pixel / CAPI)</h3>
      <div className="form-grid">
        <div className="field"><label>Meta Pixel ID</label><input value={cfg.meta_pixel_id ?? ""} onChange={(e) => setCfg((c) => ({ ...c, meta_pixel_id: e.target.value }))} placeholder="123456789012345" data-testid="cfg-pixel-id" /></div>
        <div className="field"><label>Token CAPI (serveur)</label><input value={cfg.meta_capi_token ?? ""} onChange={(e) => setCfg((c) => ({ ...c, meta_capi_token: e.target.value }))} placeholder="EAAB…" data-testid="cfg-capi-token" /></div>
      </div>
      <p className="muted" style={{ fontSize: "0.8rem", marginTop: "0.5rem" }}>Le Pixel se charge uniquement après consentement. Laissez vide pour désactiver le tracking.</p>

      <h3 className="h3" style={{ margin: "1.5rem 0 1rem" }}>Compteurs publics <span className="muted" style={{ fontSize: "0.8rem", fontWeight: 400 }}>(preuve obligatoire)</span></h3>
      <div className="form-grid">
        <div className="field"><label>Vidéos produites</label><input type="number" value={cfg.counters.videos_produites ?? ""} onChange={(e) => setCnt("videos_produites", e.target.value)} data-testid="cfg-videos" /></div>
        <div className="field"><label>Comptes actifs</label><input type="number" value={cfg.counters.comptes_actifs ?? ""} onChange={(e) => setCnt("comptes_actifs", e.target.value)} /></div>
      </div>
      <div style={{ marginTop: "0.75rem", display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
        <label className="btn btn--ghost" style={{ minHeight: 44, cursor: "pointer" }} data-testid="cfg-proof-upload-label">
          <Upload size={15} /> {uploading ? "Envoi…" : "Téléverser une preuve"}
          <input type="file" accept="image/*" style={{ display: "none" }} onChange={onUpload} data-testid="cfg-proof-upload" />
        </label>
        {cfg.counters.proof_url && <a href={cfg.counters.proof_url} target="_blank" rel="noreferrer" className="mono" style={{ color: "var(--brand-violet)", fontSize: "0.8rem" }} data-testid="cfg-proof-link">Voir la preuve</a>}
      </div>

      <h3 className="h3" style={{ margin: "1.5rem 0 1rem" }}>Hypothèses du bilan (audit)</h3>
      <div className="form-grid">
        <div className="field"><label>Taux horaire de référence (CHF/h)</label><input type="number" min="0" value={cfg.bilan?.taux_horaire ?? 50} onChange={(e) => setBilan("taux_horaire", e.target.value)} data-testid="cfg-bilan-taux" /></div>
        <div className="field"><label>Prix VIRALYA de référence (CHF/mois)</label><input type="number" min="0" value={cfg.bilan?.prix_viralya_ref ?? 197} onChange={(e) => setBilan("prix_viralya_ref", e.target.value)} data-testid="cfg-bilan-prix" /></div>
      </div>
      <p className="muted" style={{ fontSize: "0.8rem", marginTop: "0.5rem" }}>Utilisées pour chiffrer le temps récupéré et le bénéfice dans chaque bilan d'audit.</p>

      <h3 className="h3" style={{ margin: "1.5rem 0 1rem" }}>Agences — prix & marge revendeur</h3>
      <div className="form-grid">
        <div className="field"><label>Remise revendeur (%)</label><input type="number" min="0" max="90" value={cfg.agences?.remise_pct ?? 30} onChange={(e) => setRemise(e.target.value)} data-testid="cfg-agences-remise" /></div>
      </div>
      <div style={{ marginTop: "0.75rem" }}>
        <label className="mono muted" style={{ fontSize: "0.75rem", textTransform: "uppercase" }}>Plans (prix public CHF/mois)</label>
        {plans.map((p, i) => (
          <div key={i} style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem", alignItems: "center" }}>
            <input style={{ flex: 2 }} placeholder="Nom du plan" value={p.nom} onChange={(e) => setPlan(i, "nom", e.target.value)} data-testid={`cfg-plan-nom-${i}`} />
            <input style={{ flex: 1 }} type="number" placeholder="CHF" value={p.prix} onChange={(e) => setPlan(i, "prix", e.target.value)} data-testid={`cfg-plan-prix-${i}`} />
            <button className="icon-btn" style={{ width: 36, height: 36 }} onClick={() => removePlan(i)} aria-label="Retirer"><X size={14} /></button>
          </div>
        ))}
        <button className="btn btn--ghost" style={{ marginTop: "0.6rem", minHeight: 40 }} onClick={addPlan} data-testid="cfg-add-plan"><Plus size={14} /> Ajouter un plan</button>
      </div>

      <button className="btn btn--primary" style={{ marginTop: "1.4rem" }} onClick={save} data-testid="cfg-save">Enregistrer</button>
      {msg && <p className={`form-status ${msg.ok ? "ok" : "err"}`} data-testid="cfg-msg">{msg.t}</p>}
    </div>
  );
}

const EMPTY_ANGLE = { slug: "", secteur: "", titre: "", hook: "", verdict: "test", actif: true, blocks: [] };
const VERDICTS = ["test", "greenlight", "retest", "kill"];

function AngleEditor({ angle, onClose, onSaved }) {
  const [f, setF] = useState({ ...angle, blocks: angle.blocks || [] });
  const [err, setErr] = useState(null);
  const setBlock = (i, k, v) => setF((s) => { const b = [...s.blocks]; b[i] = { ...b[i], [k]: v }; return { ...s, blocks: b }; });
  const addBlock = () => setF((s) => ({ ...s, blocks: [...s.blocks, { h: "", p: "" }] }));
  const rmBlock = (i) => setF((s) => ({ ...s, blocks: s.blocks.filter((_, j) => j !== i) }));
  const save = async () => {
    try {
      await adminUpdateAngle(angle.id, { slug: f.slug, secteur: f.secteur, titre: f.titre, hook: f.hook, verdict: f.verdict, actif: f.actif, cta: f.cta, blocks: f.blocks.filter((b) => b.h || b.p) });
      onSaved();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="glass card" style={{ marginBottom: "1rem", borderColor: "var(--brand-violet)" }} data-testid={`angle-editor-${angle.slug}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
        <h3 className="h3">Éditer /o/{angle.slug}</h3>
        <button className="icon-btn" style={{ width: 36, height: 36 }} onClick={onClose} aria-label="Fermer"><X size={15} /></button>
      </div>
      <div className="form-grid">
        <div className="field"><label>Secteur</label><input value={f.secteur} onChange={(e) => setF({ ...f, secteur: e.target.value })} data-testid="edit-secteur" /></div>
        <div className="field"><label>Verdict</label><select value={f.verdict} onChange={(e) => setF({ ...f, verdict: e.target.value })} data-testid="edit-verdict">{VERDICTS.map((v) => <option key={v} value={v}>{v}</option>)}</select></div>
        <div className="field full"><label>Titre</label><input value={f.titre} onChange={(e) => setF({ ...f, titre: e.target.value })} data-testid="edit-titre" /></div>
        <div className="field full"><label>Hook</label><input value={f.hook} onChange={(e) => setF({ ...f, hook: e.target.value })} data-testid="edit-hook" /></div>
      </div>
      <label className="mono muted" style={{ fontSize: "0.75rem", textTransform: "uppercase", display: "block", marginTop: "1rem" }}>Blocs</label>
      {f.blocks.map((b, i) => (
        <div key={i} className="glass" style={{ padding: "0.9rem", marginTop: "0.6rem" }} data-testid={`edit-block-${i}`}>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <input style={{ flex: 1 }} placeholder="Titre du bloc" value={b.h} onChange={(e) => setBlock(i, "h", e.target.value)} data-testid={`edit-block-h-${i}`} />
            <button className="icon-btn" style={{ width: 36, height: 36 }} onClick={() => rmBlock(i)} aria-label="Retirer"><X size={14} /></button>
          </div>
          <textarea style={{ width: "100%", marginTop: "0.5rem" }} rows={2} placeholder="Texte" value={b.p} onChange={(e) => setBlock(i, "p", e.target.value)} data-testid={`edit-block-p-${i}`} />
        </div>
      ))}
      <button className="btn btn--ghost" style={{ marginTop: "0.6rem", minHeight: 40 }} onClick={addBlock} data-testid="edit-add-block"><Plus size={14} /> Ajouter un bloc</button>
      <div style={{ marginTop: "1.2rem" }}>
        <button className="btn btn--primary" onClick={save} data-testid="edit-save">Enregistrer</button>
        {err && <p className="form-status err">{err}</p>}
      </div>
    </div>
  );
}

function AnglesPanel() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(EMPTY_ANGLE);
  const [msg, setMsg] = useState(null);
  const [editing, setEditing] = useState(null);
  const reload = () => adminAngles().then(setRows).catch(() => setRows([]));
  useEffect(() => { reload(); }, []);

  const create = async (e) => {
    e.preventDefault(); setMsg(null);
    try { await adminCreateAngle({ ...form, blocks: [] }); setForm(EMPTY_ANGLE); reload(); }
    catch (err) { setMsg(formatApiError(err.response?.data?.detail)); }
  };
  const toggle = async (a) => { await adminUpdateAngle(a.id, { ...a, actif: !a.actif }); reload(); };
  const remove = async (a) => { await adminDeleteAngle(a.id); reload(); };

  return (
    <div data-testid="admin-angles">
      <form className="glass card" onSubmit={create} style={{ marginBottom: "1.5rem" }} data-testid="angle-create-form">
        <h3 className="h3" style={{ marginBottom: "1rem" }}>Nouvel angle</h3>
        <div className="form-grid">
          <div className="field"><label>Slug</label><input required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="angle-slug" /></div>
          <div className="field"><label>Secteur</label><input required value={form.secteur} onChange={(e) => setForm({ ...form, secteur: e.target.value })} data-testid="angle-secteur" /></div>
          <div className="field full"><label>Titre</label><input required value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} data-testid="angle-titre" /></div>
          <div className="field full"><label>Hook</label><input required value={form.hook} onChange={(e) => setForm({ ...form, hook: e.target.value })} data-testid="angle-hook" /></div>
          <div className="field"><label>Verdict</label>
            <select value={form.verdict} onChange={(e) => setForm({ ...form, verdict: e.target.value })} data-testid="angle-verdict-select">
              {VERDICTS.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        </div>
        <button className="btn btn--primary" style={{ marginTop: "1rem" }} data-testid="angle-create-submit"><Plus size={15} /> Créer l'angle</button>
        {msg && <p className="form-status err">{msg}</p>}
      </form>

      {editing && <AngleEditor angle={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}

      {!rows ? <p className="muted">Chargement…</p> : (
        <div className="glass" style={{ overflowX: "auto" }}>
          <table className="data-table">
            <thead><tr><th>Slug</th><th>Secteur</th><th>Verdict</th><th>Blocs</th><th>Actif</th><th></th></tr></thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} data-testid={`angle-row-${a.slug}`}>
                  <td className="mono">/o/{a.slug}</td>
                  <td>{a.secteur}</td>
                  <td><span className={`tag`} style={{ background: "var(--grad-soft)", color: "var(--brand-violet)" }}>{a.verdict}</span></td>
                  <td className="tnum">{(a.blocks || []).length}</td>
                  <td><button className="tag" onClick={() => toggle(a)} data-testid={`angle-toggle-${a.slug}`}>{a.actif ? "Oui" : "Non"}</button></td>
                  <td style={{ display: "flex", gap: "0.4rem" }}>
                    <button className="icon-btn" style={{ width: 36, height: 36 }} onClick={() => setEditing(a)} data-testid={`angle-edit-${a.slug}`} aria-label="Éditer"><Pencil size={15} /></button>
                    <button className="icon-btn" style={{ width: 36, height: 36 }} onClick={() => remove(a)} data-testid={`angle-delete-${a.slug}`} aria-label="Supprimer"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BilansPanel() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const reload = () => adminAudits().then(setRows).catch(() => setRows([]));
  useEffect(() => { reload(); }, []);

  const toggleSent = async (l) => {
    try { await setBilanStatus(l.id, l.bilan_statut !== "envoye"); reload(); } catch { /* ignore */ }
  };
  const mailto = (l) => {
    const b = l.bilan || {};
    const subject = "Votre bilan de situation VIRALYA";
    const body = `Bonjour ${l.nom || ""},\n\nMerci pour votre demande de bilan. Voici la synthèse de votre situation :\n\n• Temps récupéré estimé : ${b.temps_recupere_mois ?? "—"} h/mois\n• Bénéfice mensuel estimé : ${b.benefice_total_mois ?? "—"} CHF\n• Sur 12 mois : ${b.benefice_annuel ?? "—"} CHF\n\nVous trouverez votre bilan complet en pièce jointe (PDF).\n\nBien à vous,\nL'équipe VIRALYA`;
    return `mailto:${l.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (!rows) return <p className="muted">Chargement…</p>;
  if (rows.length === 0) return <p className="muted" data-testid="admin-bilans-empty">Aucun bilan pour l'instant.</p>;

  return (
    <div className="glass" style={{ overflowX: "auto" }} data-testid="admin-bilans">
      <table className="data-table">
        <thead><tr><th>Date</th><th>Prospect</th><th>Temps/mois</th><th>Bénéfice/mois</th><th>Sur 12 mois</th><th>Statut</th><th>Actions</th></tr></thead>
        <tbody>
          {rows.map((l) => {
            const b = l.bilan || {};
            const sent = l.bilan_statut === "envoye";
            const isOpen = open === l.id;
            return (
              <>
                <tr key={l.id} data-testid={`bilan-row-${l.id}`}>
                  <td className="mono">{(l.created_at || "").slice(0, 10)}</td>
                  <td><strong>{l.nom}</strong><br /><span className="muted" style={{ fontSize: "0.8rem" }}>{l.email}</span></td>
                  <td className="tnum">{b.temps_recupere_mois ?? "—"} h</td>
                  <td className="tnum">{b.benefice_total_mois != null ? chf(b.benefice_total_mois) : "—"}</td>
                  <td className="tnum">{b.benefice_annuel != null ? chf(b.benefice_annuel) : "—"}</td>
                  <td>
                    <button className={`tag ${sent ? "paye" : "en_attente"}`} onClick={() => toggleSent(l)} data-testid={`bilan-status-${l.id}`} style={{ cursor: "pointer" }}>
                      {sent ? <><Check size={12} /> Envoyé</> : "À envoyer"}
                    </button>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                      <a className="icon-btn" style={{ width: 34, height: 34 }} href={`${BACKEND}/api/admin/leads/${l.id}/bilan.pdf`} target="_blank" rel="noreferrer" title="Télécharger le PDF" data-testid={`bilan-pdf-${l.id}`}><Download size={15} /></a>
                      <a className="icon-btn" style={{ width: 34, height: 34 }} href={mailto(l)} title="Préparer l'e-mail" data-testid={`bilan-mail-${l.id}`}><Mail size={15} /></a>
                      <button className="icon-btn" style={{ width: 34, height: 34 }} onClick={() => setOpen(isOpen ? null : l.id)} title="Détail" data-testid={`bilan-detail-${l.id}`}><ChevronDown size={15} style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }} /></button>
                    </div>
                  </td>
                </tr>
                {isOpen && (
                  <tr key={l.id + "-d"}>
                    <td colSpan={7} style={{ background: "var(--grad-soft)" }}>
                      <div style={{ padding: "0.5rem 0.25rem", display: "grid", gap: "1rem", gridTemplateColumns: "1fr 1fr" }}>
                        <div>
                          <h4 className="mono muted" style={{ fontSize: "0.72rem", textTransform: "uppercase", marginBottom: "0.5rem" }}>Réponses</h4>
                          <ul style={{ margin: 0, paddingLeft: "1.1rem", fontSize: "0.88rem" }}>
                            {l.telephone && <li>Téléphone : {l.telephone}</li>}
                            {l.entreprise && <li>Entreprise : {l.entreprise}</li>}
                            <li>Heures contenu : {l.heures_contenu || 0} h/sem</li>
                            <li>Budget mensuel : {l.budget_mensuel || 0} CHF</li>
                            <li>Plateformes : {l.plateformes_nb || 0}</li>
                            <li>Fréquence visée : {l.frequence || 0}/sem</li>
                            <li>Leads/ventes actuels : {l.ventes_actuelles || 0}/mois</li>
                            {l.message && <li>Message : {l.message}</li>}
                          </ul>
                        </div>
                        <div>
                          <h4 className="mono muted" style={{ fontSize: "0.72rem", textTransform: "uppercase", marginBottom: "0.5rem" }}>Problèmes & solutions</h4>
                          <ul style={{ margin: 0, paddingLeft: "1.1rem", fontSize: "0.85rem" }}>
                            {(b.problemes || []).map((p, i) => <li key={i}>{p}</li>)}
                          </ul>
                          <ul style={{ margin: "0.5rem 0 0", paddingLeft: "1.1rem", fontSize: "0.85rem" }}>
                            {(b.solutions || []).map((s, i) => <li key={i}><strong>{s.titre}</strong> — {[s.temps, s.argent].filter((x) => x && x !== "—").join(" · ")}</li>)}
                          </ul>
                          {b.bilan_envoye_le && <p className="muted" style={{ fontSize: "0.78rem", marginTop: "0.5rem" }}>Envoyé le {l.bilan_envoye_le?.slice(0, 10)}</p>}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [tab, setTab] = useState("dashboard");

  if (user === undefined) return <div className="login-wrap"><p className="muted">Chargement…</p></div>;
  if (!user) return <Login />;

  const tabs = [
    ["dashboard", fr.admin.nav.dashboard], ["content", "Contenu (pages)"], ["angles", fr.admin.nav.angles],
    ["leads", fr.admin.nav.leads], ["orders", fr.admin.nav.orders],
    ["bilans", "Bilans"],
    ["partners", fr.admin.nav.partners], ["challenge", fr.admin.nav.challenge],
    ["config", fr.admin.nav.config],
  ];

  return (
    <div className="admin-shell" data-testid="admin-shell">
      <aside className="admin-side">
        <Link to="/" className="logo"><span className="logo__mark"><Sparkles size={16} /></span>{fr.brand.name}</Link>
        <nav>
          {tabs.map(([k, l]) => (
            <button key={k} className={tab === k ? "active" : ""} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}>{l}</button>
          ))}
        </nav>
        <div style={{ marginTop: "2rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <button className="icon-btn" onClick={toggle} style={{ width: "100%", justifyContent: "flex-start", gap: 8, padding: "0.6rem 0.9rem" }}>
            {theme === "dark" ? "Clair" : "Sombre"}
          </button>
          <button className="btn btn--ghost" onClick={logout} data-testid="admin-logout"><LogOut size={15} /> {fr.admin.logout}</button>
        </div>
      </aside>
      <main className="admin-main">
        <h1 className="h2" style={{ marginBottom: "1.5rem" }}>{tabs.find((t) => t[0] === tab)[1]}</h1>
        {tab === "dashboard" && <Dashboard />}
        {tab === "content" && <ContentPanel />}
        {tab === "angles" && <AnglesPanel />}
        {tab === "leads" && (
          <DataList testid="admin-leads-table" load={adminLeads} columns={["Date", "Nom", "E-mail", "Type", "Entreprise", "Bilan"]}
            render={(l) => (<tr key={l.id}><td className="mono">{(l.created_at || "").slice(0, 10)}</td><td>{l.nom}</td><td>{l.email}</td><td>{l.type || l.profil || "—"}</td><td>{l.entreprise || "—"}</td><td>{l.type === "audit" ? (<a className="af-link" href={`${BACKEND}/api/admin/leads/${l.id}/bilan.pdf`} target="_blank" rel="noreferrer" data-testid={`lead-bilan-${l.id}`}>PDF</a>) : "—"}</td></tr>)} />
        )}
        {tab === "orders" && (
          <DataList testid="admin-orders-table" load={adminOrders} columns={["Date", "Nom", "E-mail", "Bump", "Montant", "Statut"]}
            render={(o) => (<tr key={o.id}><td className="mono">{(o.created_at || "").slice(0, 10)}</td><td>{o.nom}</td><td>{o.email}</td><td>{o.bump ? "Oui" : "Non"}</td><td className="tnum">{chf(o.montant)}</td><td><span className={`tag ${o.statut}`}>{o.statut}</span></td></tr>)} />
        )}
        {tab === "partners" && (
          <DataList testid="admin-partners-table" load={adminPartners} columns={["Date", "Nom", "E-mail", "Agence", "Clients"]}
            render={(p) => (<tr key={p.id}><td className="mono">{(p.created_at || "").slice(0, 10)}</td><td>{p.nom}</td><td>{p.email}</td><td>{p.agence}</td><td>{p.clients || "—"}</td></tr>)} />
        )}
        {tab === "challenge" && (
          <DataList testid="admin-challenge-table" load={adminChallenge} columns={["Date", "Nom", "E-mail", "Profil"]}
            render={(c) => (<tr key={c.id}><td className="mono">{(c.created_at || "").slice(0, 10)}</td><td>{c.nom}</td><td>{c.email}</td><td>{c.profil || "—"}</td></tr>)} />
        )}
        {tab === "bilans" && <BilansPanel />}
        {tab === "config" && <ConfigPanel />}
      </main>
    </div>
  );
}
